import React from 'react';
import { X, Image as ImageIcon, MapPin, Tag, Calendar, Edit3, ArrowUpRight, Box } from 'lucide-react';
import { formatExpiryDate, getExpiryStatus } from '../../utils/date.js';
import { formatBoxContentsSummary } from '../../utils/box-contents.js';
import { getProductGroupKey } from '../../utils/sorting.js';

export function ItemDetailModal({ 
  isOpen, 
  item, 
  onClose, 
  onEditItem, 
  onBorrowItem 
}) {
  if (!isOpen || !item) return null;

  const imageUrl = item.imageUrl || item.imageBase64 || item.image;
  const expiryStatus = getExpiryStatus(item.expiryDate);
  const expiryText = formatExpiryDate(item.expiryDate);
  const boxSummary = formatBoxContentsSummary(item.boxContents);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-150 my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
              <Box className="w-4 h-4" />
            </div>
            <h2 className="text-base font-bold text-slate-900 truncate">รายละเอียดพัสดุ</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Image */}
          <div className="w-full h-52 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center relative">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.name}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-400 gap-1.5">
                <ImageIcon className="w-8 h-8" />
                <span className="text-xs font-medium">ไม่มีรูปภาพสินค้า</span>
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h3 className="text-xl font-extrabold text-slate-900 leading-snug">{item.name}</h3>
            <div className="flex flex-wrap gap-2 mt-2">
              {item.location && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-cyan-800 bg-cyan-100/70 border border-cyan-200/80 px-2.5 py-1 rounded-lg">
                  <MapPin className="w-3.5 h-3.5 text-cyan-700" />
                  <span>{item.location}</span>
                </span>
              )}
              {item.brand && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-800 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg">
                  <Tag className="w-3.5 h-3.5 text-slate-600" />
                  <span>แบรนด์: {item.brand}</span>
                </span>
              )}
              {item.size && (
                <span className="inline-flex items-center text-xs font-bold text-slate-800 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg">
                  ขนาด: {item.size}
                </span>
              )}
            </div>
          </div>

          {/* Lot & Expiry Card */}
          <div className="rounded-xl border border-slate-300/80 bg-slate-50/80 p-3.5 space-y-2 text-xs">
            <div className="flex justify-between items-center">
              <span className="text-slate-600 font-semibold">ล็อตอ้างอิง:</span>
              <span className="font-extrabold text-slate-900">{item.lot || 'ล็อตหลัก'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 font-semibold">จำนวนคงเหลือในล็อต:</span>
              <span className="font-extrabold text-indigo-700 text-sm">
                {item.quantity} {item.unit || 'ชิ้น'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600 font-semibold">วันหมดอายุ:</span>
              <span className={`font-bold ${
                expiryStatus === 'expired'
                  ? 'text-red-700'
                  : expiryStatus === 'expiring-soon'
                  ? 'text-amber-800'
                  : 'text-slate-800'
              }`}>
                {expiryText || 'ไม่ระบุวันหมดอายุ'}
              </span>
            </div>
            {boxSummary && (
              <div className="flex justify-between items-center pt-1 border-t border-slate-200/80">
                <span className="text-slate-600 font-semibold">บรรจุในกล่องย่อย:</span>
                <span className="font-bold text-indigo-700">{boxSummary}</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {item.notes && (
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-1">หมายเหตุเพิ่มเติม:</div>
              <p className="text-xs text-slate-700 bg-slate-50 border border-slate-100 p-2.5 rounded-lg whitespace-pre-wrap">
                {item.notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={() => {
              onClose();
              onEditItem(item.id);
            }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-xs transition-colors cursor-pointer"
          >
            <Edit3 className="w-3.5 h-3.5" />
            <span>แก้ไขข้อมูล</span>
          </button>

          <button
            onClick={() => {
              onClose();
              onBorrowItem(getProductGroupKey(item));
            }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-500 text-slate-900 font-bold text-xs shadow-xs transition-colors cursor-pointer"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
            <span>ยืมสินค้านี้</span>
          </button>
        </div>
      </div>
    </div>
  );
}
